import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ACCOUNT_REPORT_TYPES, EAccountReportStatus, IAccount, IAccountUpdateResult } from '../models/account.model';
import { HttpClient, HttpParams } from '@angular/common/http';
import { IInboxManagementSettings, IIntegrationData, UserType } from '../models/user.model';
import { IBaseTableResult } from '../models/table.model';
import { environment } from '../../../environments/environment';
import { IVsmGroup } from '../models/vsm.model';
import { tap } from 'rxjs/operators';
import { IInvoiceDownload } from '../../accounts/invoices/invoice.model';

@Injectable({
  providedIn: 'root',
})
export class AccountService {
  constructor(private http: HttpClient) {
  }

  exportContacts(
    accountId: number,
    format: 'csv',
    groupId?: number,
  ): Observable<any> {
    const payload: {
      format: any;
      group_id?: string;
    } = {
      format,
    };

    if (groupId) {
      payload.group_id = groupId.toString();
    }
    return this.http.get(
      environment.apiUrl + `/v1/contacts/${accountId}/export`,
      {
        params: payload,
        observe: 'response',
        responseType: 'blob' as 'json',
      },
    );
  }

  getAccountsBasicInfo(
    accountType?: UserType,
  ): Observable<IBaseTableResult<IAccount>> {
    const params: {
      user_type?: UserType;
      paginated: string;
      basic_info: string;
    } = {
      paginated: 'false',
      basic_info: 'true',
    };

    if (accountType) {
      params.user_type = accountType;
    }

    return this.http.get<IBaseTableResult<IAccount>>(
      environment.apiUrl + '/v1/users',
      {
        params,
      },
    );
  }

  getManagers(): Observable<IBaseTableResult<IAccount>> {
    return this.getAccountsBasicInfo(UserType.MANAGER);
  }

  getPartners(): Observable<IBaseTableResult<IAccount>> {
    return this.getAccountsBasicInfo(UserType.PARTNER);
  }

  getVSMs(): Observable<IBaseTableResult<IVsmGroup>> {
    return this.http.get<IBaseTableResult<IVsmGroup>>(
      environment.apiUrl + '/v1/vsm',
    );
  }

  setAccountManager(accountId: number, managerId: number, subscriptionPaidByManager: boolean = true): Observable<IAccountUpdateResult> {
    return this.http.put<IAccountUpdateResult>(
      environment.apiUrl + `/v1/users/${accountId}/manager`,
      {
        manager_id: managerId,
        paid_by_manager: subscriptionPaidByManager,
      },
    );
  }

  setAccountPartner(accountId, partnerId): Observable<IAccountUpdateResult> {
    return this.http.put<IAccountUpdateResult>(
      environment.apiUrl + `/v1/users/${accountId}`,
      {
        partner_id: partnerId,
      },
    );
  }

  setAccountVsm(
    accountId: number,
    vsmId: number,
  ): Observable<IAccountUpdateResult> {
    return this.http.put<IAccountUpdateResult>(
      environment.apiUrl + `/v1/users/${accountId}`,
      {
        vsm_group_id: vsmId,
      },
    );
  }

  updateUserDetails(accountId, details): Observable<IAccountUpdateResult> {
    return this.http.put<IAccountUpdateResult>(
      environment.apiUrl + `/v1/users/${accountId}`,
      details,
    );
  }

  confirmUser(
    accountId: number,
    setInvoiced: boolean = false,
  ): Observable<{
    success: boolean;
  }> {
    return this.http.put<{
      success: boolean;
    }>(environment.apiUrl + `/v1/users/${accountId}/confirm`, {
      set_invoiced: setInvoiced,
    });
  }

  createAccount(
    email: string,
    firstName: string,
    lastName: string,
    company: string,
    userType: UserType,
  ): Observable<{
    account_id: number;
  }> {
    return this.http.post<{
      account_id: number;
    }>(environment.apiUrl + '/v1/users', {
      email,
      first_name: firstName,
      last_name: lastName,
      company,
      user_type: userType,
    });
  }

  switchAccount(accountId) {
    return this.http
      .get<any>(environment.apiUrl + `/v1/users/switch/${accountId}`, {})
      .pipe(
        tap(() => {
          const oldTokensData = localStorage.getItem('oldTokens');
          let oldTokens = [];
          if (oldTokensData) {
            oldTokens = JSON.parse(oldTokensData);
          }
          const oldToken = JSON.parse(localStorage.getItem('token'));

          if (
            oldTokens.filter(t => t.access_token === oldToken.access_token)
              .length === 0
          ) {
            oldTokens.push(oldToken);
          }

          localStorage.setItem('oldTokens', JSON.stringify(oldTokens));
        }),
      );
  }

  switchBack() {
    return this.http.get<any>(
      environment.apiUrl + `/v1/users/logout?return_original=true`,
      {},
    );
  }

  getBcc(type) {
    return this.http.get<any>(environment.apiUrl + `/v1/bcc-emails/${type}`);
  }

  setBcc(
    type,
    form,
  ): Observable<{
    success: boolean;
  }> {
    return this.http.put<{
      success: boolean;
    }>(environment.apiUrl + `/v1/bcc-emails/${type}`, {
      email: form.email,
    });
  }

  getTags() {
    return this.http.get<any>(environment.apiUrl + `/v1/tags`);
  }

  changeAccountSetting(
    accountId: number,
    settings: any,
  ): Observable<{
    setting: number;
  }> {
    return this.http.put<{
      setting: number;
    }>(environment.apiUrl + `/v1/settings/${accountId}`, settings);
  }

  sendForgotPasswordEmail(email: string): Observable<{
    success: boolean;
    message?: string;
  }> {
    return this.http.post<{
      success: boolean;
      message?: string;
    }>(environment.apiUrl + '/v1/users/forgotten_password', {
      email,
    });
  }

  toggleAccountInboxSettings(accountId: number): Observable<{
    success: boolean;
  }> {
    return this.http.put<{
      success: boolean;
    }>(environment.apiUrl + `/v1/settings/inbox_toggle`, {
      account_id: accountId,
    });
  }

  updateInboxManagementSettings(
    accountId: number,
    settings: IInboxManagementSettings,
  ): Observable<{
    success: boolean;
  }> {
    return this.http.put<{
      success: boolean;
    }>(environment.apiUrl + `/v1/settings/${accountId}/inbox`, {
      ...settings,
    });
  }

  resetPassword(
    token: string,
    newPassword: string,
    confirmPassword: string,
  ): Observable<{ account_id: number }> {
    return this.http.post<{ account_id: number }>(environment.apiUrl + '/v1/users/reset_password', {
      token,
      new_password: newPassword,
      confirm_password: confirmPassword,
    });
  }

  getAccountById(accountId: number): Observable<IAccount> {
    return this.http.get<IAccount>(
      environment.apiUrl + `/v1/users/${accountId}`,
    );
  }

  restoreAccount(accountId: number): Observable<{
    success: boolean;
  }> {
    return this.http.put<{
      success: boolean;
    }>(environment.apiUrl + `/v1/users/${accountId}/restore`, {});
  }

  deleteAccount(
    accountId: number,
    softDelete: boolean = false,
  ): Observable<{
    success: boolean;
  }> {
    return this.http.delete<{
      success: boolean;
    }>(environment.apiUrl + `/v1/users/${accountId}`, {
      params: {
        force_delete: softDelete.toString(),
      },
    });
  }

  downloadBouncedReport(accountId?: number): Observable<Blob> {
    const params: {
      report: string;
      account_id?: string;
    } = {
      report: 'true',
    };

    if (accountId) {
      params.account_id = accountId.toString();
    }
    return this.http.get<Blob>(
      environment.apiUrl + `/v1/users/bounced_emails`,
      {
        responseType: 'blob' as 'json',
        params,
      },
    );
  }

  getIntegrationMap(integrationId: number): Observable<IIntegrationData> {
    return this.http.get<IIntegrationData>(environment.apiUrl + `/v1/integrations/${integrationId}/map`, {});
  }

  createIntegration(integrationId, data: any): Observable<any> {
    return this.http.post(environment.apiUrl + `/v1/integrations/${integrationId}/map`, data);
  }

  updateIntegration(integrationId: number, data: any): Observable<any> {
    return this.http.put(environment.apiUrl + `/v1/integrations/${integrationId}`, data);
  }

  downloadContactsReport(reportType: EAccountReportStatus, accountId: number[], download: boolean = true, format: string = 'xls'): Observable<any> {
    const options: any = {};
    const params: any = {};

    if (download) {
      options.responseType = 'blob' as 'json';
      options.observe = 'response';
    }
    params.format = format;
    params.account_id = accountId.join(',');

    // TODO: fix this for later use, when can do xls
    if (format === 'csv') {
      params.type = ACCOUNT_REPORT_TYPES[reportType];
    }

    return this.http.get<any>(environment.apiUrl + `/v1/users/report`, {
      ...options,
      params: new HttpParams({
        fromObject: params,
      }),
    });
  }

  getInvoiceObject(invoiceId: number): Observable<IInvoiceDownload[]> {
    return this.http.get<IInvoiceDownload[]>(environment.apiUrl + '/v1/subscriptions/invoices/' + invoiceId);
  }
}
